import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate } from '#hooks/useNavigate';

import { cerrarTodoConAtras, useCerrarConAtras } from './atras';
import { ConfirmarHoja } from './ConfirmarHoja';
import { Hoja } from './Hoja';

type AvisoSalida = {
  /** La hoja «¿Descartar lo escrito?» (o nada): va en la pantalla. */
  aviso: ReactNode;
  /**
   * Para salir a propósito (al guardar o borrar): quita el aviso y hace
   * `accion` cuando el historial ya está en su sitio.
   */
  salir: (accion: () => void) => void;
};

/**
 * Mientras haya algo escrito sin guardar (`sucio`), «atrás» (y la flecha
 * «Volver», que también va atrás) no sale de la pantalla: pregunta antes
 * si se descarta. «Descartar» sale; «Cancelar» o «atrás» otra vez sigue
 * editando.
 */
export function useAvisoSalida(
  sucio: boolean,
  textos?: { pregunta: string; detalle: string; accion: string },
): AvisoSalida {
  const { t } = useTranslation();
  const pregunta = textos?.pregunta ?? t('Discard what you wrote?');
  const navigate = useNavigate();
  const [preguntando, setPreguntando] = useState(false);
  const [saliendo, setSaliendo] = useState(false);
  const cerrarYLuego = useCerrarConAtras(
    sucio && !preguntando && !saliendo,
    () => setPreguntando(true),
  );

  const salir = (accion: () => void) => {
    setSaliendo(true);
    cerrarYLuego(accion);
  };

  const descartar = () => {
    setSaliendo(true);
    // La hoja se cierra y, con el historial ya en su sitio, se sale.
    cerrarTodoConAtras(() => void navigate(-1));
  };

  const aviso = preguntando && (
    <Hoja
      aria-label={pregunta}
      data-testid="aviso-salida"
      onCerrar={() => setPreguntando(false)}
    >
      <ConfirmarHoja
        icono="trash"
        pregunta={pregunta}
        detalle={
          textos?.detalle ??
          t('If you leave now, what you have not saved will be lost.')
        }
        textoAccion={textos?.accion ?? t('Discard')}
        testIdAccion="aviso-salida-descartar"
        onConfirmar={descartar}
        onCancelar={() => setPreguntando(false)}
      />
    </Hoja>
  );

  return { aviso, salir };
}
